// Pieces the warmup board and one pool's page share: a day's rates against
// rule 4, the decision a day was given, the provider split, and the notice a
// stopped pool carries. Every word and tone comes from `model.ts`.

import type {
  WarmupDecision,
  WarmupFigures,
  WarmupIsp,
  WarmupPool,
  WarmupRates,
  WarmupThresholds,
} from "@/lib/api";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout } from "@/components/emc/states";
import { count } from "@/components/emc/format";
import { cn } from "@/lib/utils";
import { ispShares, rateCell, repauseWarning, stopReason, thresholdLine } from "@/pages/warmup/model";
import { shortAtViewer } from "@/components/emc/time";
import { reasonText } from "@/pages/warmup/reasons";
import type { RateKind } from "@/pages/warmup/model";

export function RatePill({
  rates,
  kind,
  minSample,
}: {
  rates: WarmupRates | null;
  kind: RateKind;
  minSample: number;
}) {
  const cell = rateCell(rates, kind, minSample);
  return (
    <StatusPill tone={cell.tone} title={cell.title}>
      {cell.text}
    </StatusPill>
  );
}

/** Yesterday and today so far, bounce and complaint, and the lines they are held to. */
export function RateGrid({
  yesterday,
  today,
  thresholds,
  minSample,
  className,
}: {
  yesterday: WarmupFigures;
  today: WarmupFigures;
  thresholds: WarmupThresholds;
  minSample: number;
  className?: string;
}) {
  const rows: { label: string; figures: WarmupFigures }[] = [
    { label: yesterday.partial ? "tegnap (részleges)" : "tegnap", figures: yesterday },
    { label: "ma eddig", figures: today },
  ];
  return (
    <div
      className={cn(
        "grid grid-cols-[minmax(0,auto)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-x-4 gap-y-1.5",
        className,
      )}
    >
      <span />
      <span className="caps-label">bounce</span>
      <span className="caps-label">panasz</span>
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <span className="text-muted-foreground">{row.label}</span>
          <span>
            <RatePill rates={row.figures.rates} kind="bounce" minSample={minSample} />
          </span>
          <span>
            <RatePill rates={row.figures.rates} kind="complaint" minSample={minSample} />
          </span>
        </div>
      ))}
      <span className="text-label text-muted-foreground">küszöb</span>
      <span className="text-label text-muted-foreground">{thresholdLine("bounce", thresholds)}</span>
      <span className="text-label text-muted-foreground">{thresholdLine("complaint", thresholds)}</span>
    </div>
  );
}

/** A decision in one line: the pill, the limit it set, who and when. */
export function DecisionLine({
  decision,
  showReason = true,
}: {
  decision: WarmupDecision;
  showReason?: boolean;
}) {
  const basis = decision.basis;
  const title = basis
    ? `A döntés alapja: ${count(basis.sent)} kiment, ${count(basis.bounced)} bounce, ${count(
        basis.complained,
      )} panasz`
    : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-1" title={title}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <StatusPill domain="warmupDecision" state={decision.decision} />
        <span className="font-medium">→ {count(decision.daily_limit_per_ip)} / IP</span>
        <span className="text-label text-muted-foreground">
          {decision.actor} · {shortAtViewer(decision.decided_at)}
        </span>
        {decision.rehearsal && (
          <span className="text-label font-medium text-warn">próba — szimulált számokon</span>
        )}
      </div>
      {showReason && decision.reason && (
        <p className="text-label break-words text-muted-foreground" title={decision.reason}>
          {reasonText(decision.reason)}
        </p>
      )}
    </div>
  );
}

/** "Gmail 46% · Microsoft 22% · helyi 19%" — the three largest, then the rest. */
export function IspLine({ rows }: { rows: WarmupIsp[] }) {
  const shares = ispShares(rows);
  if (shares.length === 0) {
    return <span className="text-label text-muted-foreground">levelezőnként: nincs adat</span>;
  }
  const shown = shares.slice(0, 3);
  const rest = shares.length - shown.length;
  return (
    <span
      className="text-label text-muted-foreground"
      title={shares.map((row) => `${row.label}: ${count(row.sent)} (${row.share ?? "—"})`).join("\n")}
    >
      {shown.map((row) => `${row.label} ${row.share ?? "—"}`).join(" · ")}
      {rest > 0 ? ` · +${rest}` : ""}
    </span>
  );
}

/** What stops a pool, who and since when — the first thing a stopped card says. */
export function StoppedNotice({ pool }: { pool: WarmupPool }) {
  const watchHolds = pool.watch.state === "paused" || pool.watch.state === "throttled";
  if (!pool.is_paused && !watchHolds) return null;
  const reason = stopReason(pool) ?? "nincs indok a soron";
  const by = pool.watch.actor && watchHolds ? pool.watch.actor : null;
  const since = watchHolds ? pool.watch.since : null;
  if (!pool.is_paused) {
    return (
      <Callout tone="warn">
        <span className="font-medium">Lassítva:</span> {reason}
        {by && ` — ${by}`}
        {since && `, ${shortAtViewer(since)}`}
      </Callout>
    );
  }
  return (
    <div
      role="status"
      className="flex flex-col gap-1 rounded-inner bg-red-soft px-4 py-3 text-foreground"
    >
      <p>
        <span className="font-medium text-danger">Áll:</span> {reason}
      </p>
      {(by || since) && (
        <p className="text-label text-muted-foreground">
          {by ?? ""}
          {by && since ? " · " : ""}
          {since ? shortAtViewer(since) : ""}
        </p>
      )}
      {pool.repause_risk && (
        <p className="text-label font-medium text-warn">{repauseWarning(pool)}</p>
      )}
    </div>
  );
}
